import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

import { I18nPipe } from '../../pipes/i18n.pipe';
import { CartService } from '../../services/cart.service';
import { HeaderService } from '../../services/header.service';
import { OnlyIndicationService } from '../../../core/services/only-indication.service';
import { UtmPreservationService } from '../../../core/services/utm-preservation.service';
import { UserFacade } from '../../../core/store/user/user.facade';

export type MobileNavbarItemId =
  | 'home'
  | 'search'
  | 'categories'
  | 'subscriptions'
  | 'cart';

interface MobileNavbarItem {
  id: MobileNavbarItemId;
  labelKey: string;
  icon: string;
}

@Component({
  selector: 'umkt-mobile-navbar',
  standalone: true,
  imports: [CommonModule, I18nPipe],
  templateUrl: './mobile-navbar.component.html',
  styleUrl: './mobile-navbar.component.scss',
})
export class MobileNavbarComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly userFacade = inject(UserFacade);
  private readonly cartService = inject(CartService);
  private readonly headerService = inject(HeaderService);
  private readonly onlyIndicationService = inject(OnlyIndicationService);
  private readonly utmPreservationService = inject(UtmPreservationService);

  readonly items: MobileNavbarItem[] = [
    { id: 'home', labelKey: 'components.mobileNavbar.home', icon: 'mdn-Icon-casa' },
    { id: 'search', labelKey: 'components.mobileNavbar.search', icon: 'mdn-Icon-lupa' },
    { id: 'categories', labelKey: 'components.mobileNavbar.categories', icon: 'mdn-Icon-quatro-quadrados' },
    { id: 'subscriptions', labelKey: 'components.mobileNavbar.subscriptions', icon: 'mdn-Icon-estrela-vazia' },
    { id: 'cart', labelKey: 'components.mobileNavbar.cart', icon: 'mdn-Icon-carrinho' },
  ];

  activeItem: MobileNavbarItemId = 'home';
  isAuthenticated = false;
  isOnlyIndicationActive = false;

  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.userFacade.getIsAuthenticated().subscribe((isAuth) => {
        this.isAuthenticated = isAuth;
      })
    );

    this.subscriptions.add(
      this.onlyIndicationService.isOnlyIndicationActive$.subscribe((active) => {
        this.isOnlyIndicationActive = active;
      })
    );

    this.updateActiveFromUrl(this.router.url);
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event) => {
          const nav = event as NavigationEnd;
          this.updateActiveFromUrl(nav.urlAfterRedirects || nav.url);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onItemClick(item: MobileNavbarItem, event?: Event): void {
    this.activeItem = item.id;

    switch (item.id) {
      case 'home':
        this.navigateHome();
        break;
      case 'search':
        // Avoid the header document:click listener closing the panel on the same tap
        event?.stopPropagation();
        this.headerService.openMobileSearch();
        break;
      case 'categories':
        // Temporary until categories page ships
        this.navigateHome();
        break;
      case 'subscriptions':
        this.navigateSubscriptions();
        break;
      case 'cart':
        this.cartService.toggleCartSidebar();
        break;
    }
  }

  private navigateHome(): void {
    const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({});
    this.router.navigate(['/'], { queryParams });
    this.headerService.triggerScrollTo(null);
  }

  private navigateSubscriptions(): void {
    if (this.isAuthenticated) {
      this.router.navigate(['/my-account/plans']);
      return;
    }

    const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({});
    this.router.navigate(['/login'], { queryParams });
  }

  private updateActiveFromUrl(url: string): void {
    const path = (url || '').split('?')[0];

    if (path.startsWith('/my-account/plans')) {
      this.activeItem = 'subscriptions';
      return;
    }

    if (path === '/' || path === '') {
      this.activeItem = 'home';
      return;
    }

    // Keep previous active for search/cart overlays and other routes
  }
}
